import React, { useState, useEffect } from 'react';
import { syncManager } from '../../services/sync';
import '../../styles/RefundModal.css';

/**
 * RefundModal - Admin interface for processing refunds and credit notes
 * Supports: Full refund, Partial refund, Refund reasons
 */
const RefundModal = ({ isOpen, onClose, invoice, onSuccess }) => {
    const [refundType, setRefundType] = useState('full');
    const [refundAmount, setRefundAmount] = useState('');
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');
    const [reissueInvoice, setReissueInvoice] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const REFUND_REASONS = [
        { value: 'duplicate', label: 'Duplicate Payment' },
        { value: 'service_issue', label: 'Service Issue' },
        { value: 'customer_request', label: 'Customer Request' },
        { value: 'billing_error', label: 'Billing Error' },
        { value: 'cancellation', label: 'Subscription Cancellation' },
        { value: 'other', label: 'Other' }
    ];

    useEffect(() => {
        if (invoice) {
            setRefundAmount(invoice.amount?.toString() || '');
        }
        setRefundType('full');
        setReason('');
        setNotes('');
        setReissueInvoice(false);
        setError('');
    }, [invoice, isOpen]);

    if (!isOpen || !invoice) {return null;}

    const maxRefundable = parseFloat(invoice.amount || 0);
    const currentAmount = parseFloat(refundAmount || 0);
    const isValid = reason && currentAmount > 0 && currentAmount <= maxRefundable;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isValid) {return;}

        setLoading(true);
        setError('');

        try {
            const payload = {
                invoiceId: invoice.invoiceId,
                originalAmount: invoice.amount,
                refundAmount: currentAmount,
                refundType: refundType,
                reissueInvoice: reissueInvoice,
                reason: reason,
                reasonLabel: REFUND_REASONS.find(r => r.value === reason)?.label || reason,
                notes: notes,
                clientEmail: invoice.clientEmail,
                clientName: invoice.clientName,
                adminEmail: localStorage.getItem('BIZBUILDER_USER_EMAIL') || 'admin'
            };

            const response = await syncManager.triggerWorkflow('process-refund', payload);

            if (response?.success) {
                onSuccess?.(response);
                onClose();
            } else {
                setError(response?.message || 'Refund processing failed');
            }
        } catch (err) {
            console.error('Refund error:', err);
            setError(err.message || 'Failed to process refund');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="refund-modal-overlay" onClick={onClose}>
            <div className="refund-modal" onClick={e => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>×</button>

                <div className="modal-header">
                    <div className="header-icon">💸</div>
                    <h2>Process Refund</h2>
                    <p className="invoice-ref">Invoice: <strong>{invoice.invoiceId}</strong></p>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Invoice Summary */}
                    <div className="invoice-summary">
                        <div className="summary-row">
                            <span>Client:</span>
                            <strong>{invoice.clientName}</strong>
                        </div>
                        <div className="summary-row">
                            <span>Original Amount:</span>
                            <strong className="amount">R {maxRefundable.toFixed(2)}</strong>
                        </div>
                        <div className="summary-row">
                            <span>Payment Date:</span>
                            <strong>{invoice.date}</strong>
                        </div>
                    </div>

                    {/* Refund Type */}
                    <div className="form-group">
                        <label>Refund Type</label>
                        <div className="refund-type-selector">
                            <button
                                type="button"
                                className={`type-btn ${refundType === 'full' ? 'active' : ''}`}
                                onClick={() => {
                                    setRefundType('full');
                                    setRefundAmount(maxRefundable.toString());
                                }}
                            >
                                Full Refund
                            </button>
                            <button
                                type="button"
                                className={`type-btn ${refundType === 'partial' ? 'active' : ''}`}
                                onClick={() => {
                                    setRefundType('partial');
                                    setRefundAmount('');
                                }}
                            >
                                Partial Refund
                            </button>
                        </div>
                    </div>

                    {/* Refund Amount */}
                    {refundType === 'partial' && (
                        <>
                            <div className="form-group">
                                <label>Refund Amount</label>
                                <div className="amount-input-wrapper">
                                    <span className="currency">R</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        max={maxRefundable}
                                        value={refundAmount}
                                        onChange={(e) => setRefundAmount(e.target.value)}
                                        placeholder="0.00"
                                        required
                                    />
                                </div>
                                <span className="hint">Maximum: R {maxRefundable.toFixed(2)}</span>
                            </div>

                            <div className="form-group checkbox-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={reissueInvoice}
                                        onChange={(e) => setReissueInvoice(e.target.checked)}
                                    />
                                    <span>Void Original & Re-issue New Invoice</span>
                                </label>
                                {reissueInvoice && (
                                    <p className="hint" style={{ color: '#60a5fa', marginLeft: '28px' }}>
                                        A new invoice will be issued for R {(maxRefundable - (parseFloat(refundAmount) || 0)).toFixed(2)}.
                                        The original will be voided.
                                    </p>
                                )}
                            </div>
                        </>
                    )}

                    {/* Reason */}
                    <div className="form-group">
                        <label>Reason for Refund *</label>
                        <select
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            required
                        >
                            <option value="">Select a reason...</option>
                            {REFUND_REASONS.map(r => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Notes */}
                    <div className="form-group">
                        <label>Additional Notes</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Any additional details about this refund..."
                            rows={3}
                        />
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="error-message">
                            ⚠️ {error}
                        </div>
                    )}

                    {/* Summary */}
                    <div className="refund-summary">
                        <div className="summary-header">Refund Summary</div>
                        <div className="summary-amount">
                            <span>Amount to Refund:</span>
                            <strong className={currentAmount > 0 ? 'valid' : ''}>
                                R {currentAmount.toFixed(2)}
                            </strong>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="modal-actions">
                        <button type="button" className="btn-cancel" onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-refund"
                            disabled={!isValid || loading}
                        >
                            {loading ? '⏳ Processing...' : '💸 Process Refund'}
                        </button>
                    </div>

                    {/* Warning */}
                    <p className="warning-text">
                        ⚠️ Refunds cannot be reversed. A credit note will be generated and sent to the client.
                    </p>
                </form>
            </div>
        </div>
    );
};

export default RefundModal;
